import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRODUCT_BRAND } from "@/lib/productBranding";

/**
 * Homepage AWARE product-family cards.
 *
 * Feb 2026 — refresh with user-supplied illustrated product cards.
 * Each PACKAGES row now carries a `poster` URL pointing at the
 * dedicated AWARE {4-Point | Tank 360 | Bore 360} card artwork the
 * user provided. Those posters already carry the AWARE wordmark,
 * product number, key-parameter diagram and a one-line tagline, so
 * the previous dark hero-badge panel + supporting photo have been
 * consolidated into one square hero image. The descriptive text
 * block underneath is kept for SEO + accessibility (screen readers
 * won't read the artwork's baked-in text).
 */
const PACKAGES = [
  {
    testId: "basic",
    to: "/aware-4-point",
    blurb: "Focused screening for tank or bore water — E. coli, coliforms, pH and conductivity.",
    testid: "home-pkg-link-basic",
    // Feb 2026 (v2) — corrected artwork: "TOTAL COLIFORMS" → "COLIFORMS".
    // Local copy under /public/img/v2/ so it ships with the deploy and
    // survives even if the customer-assets CDN URL rotates.
    poster: "/img/v2/aware-4-point-square.png",
    alt: "AWARE 4-Point — starter water safety check card showing pH, E. coli, Coliforms and conductivity indicators",
  },
  {
    testId: "tank",
    to: "/aware-tank-360",
    blurb: "Our most popular rainwater tank test — full bacterial, chemical, mineral and metals profile.",
    testid: "home-pkg-link-tank",
    featured: true,
    // Feb 2026 (v2) — regenerated square poster with unified layout.
    poster: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/nq5r5l56_ChatGPT%20Image%20Jul%2014%2C%202026%2C%2005_33_00%20PM.png",
    alt: "AWARE Tank 360 — comprehensive tank water analysis card showing microbiological, inorganic, chemical, heavy metals and physical parameter categories",
  },
  {
    testId: "bore",
    to: "/aware-bore-360",
    blurb: "For bore-water properties — full bacterial, chemical, mineral and metals profile including manganese.",
    testid: "home-pkg-link-bore",
    // Feb 2026 (v2) — regenerated square poster with unified layout.
    poster: "https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/dbh6l72o_ChatGPT%20Image%20Jul%2014%2C%202026%2C%2005_34_09%20PM.png",
    alt: "AWARE Bore 360 — comprehensive bore water analysis card showing groundwater layers and full analysis categories",
  },
];

export const TestPackagesStrip = () => (
  <section
    aria-labelledby="home-packages-heading"
    data-testid="home-test-packages-strip"
    className="bg-white border-b border-slate-100 py-10 lg:py-16"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-600 mb-2">
            The AWARE Water Testing range
          </p>
          <h2
            id="home-packages-heading"
            className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-slate-900"
          >
            One clear check, or a complete 360° analysis.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl">
            AWARE 4-Point covers the essentials. AWARE Tank 360 and AWARE Bore 360 deliver the full comprehensive picture.
          </p>
        </div>
        <Link
          to="/book-water-test"
          data-testid="home-packages-book-link"
          className="btn-aqua inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold self-start lg:self-auto"
        >
          Book a Water Test <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
        {PACKAGES.map((p) => {
          const brand = PRODUCT_BRAND[p.testId];
          return (
            <Link
              key={p.to}
              to={p.to}
              data-testid={p.testid}
              className={`group flex flex-col rounded-3xl border overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1 ${
                p.featured
                  ? "border-cyan-500/70 bg-white shadow-lg"
                  : "border-slate-200 bg-white hover:border-cyan-400"
              }`}
            >
              {/* ═══════ HERO POSTER ═══════
                  User-supplied illustrated AWARE card (square 1:1).
                  The regenerated Feb-2026-v2 posters share a common
                  internal layout template (AWARE wordmark at ~5%,
                  bottom tagline band at ~90%) so all three cards
                  line up naturally when rendered side-by-side. The
                  reserved aspect container prevents CLS. */}
              <div className="relative aspect-square overflow-hidden bg-slate-50">
                <img
                  src={p.poster}
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="800"
                  className="absolute inset-0 w-full h-full object-contain"
                  data-testid={`${p.testid}-poster`}
                />
                {p.featured && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
                    Most Popular
                  </span>
                )}
              </div>

              {/* ═══════ CONTENT ═══════
                  Text kept for SEO + a11y since the poster's copy is
                  baked-in pixels that assistive tech can't read. */}
              <div className="p-5 lg:p-6 flex-1 flex flex-col border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700 mb-1">
                  {brand.description}
                </div>
                <h3 className="font-display text-xl lg:text-2xl font-bold text-slate-900 leading-tight mb-2">
                  {brand.displayName}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed italic mb-3">
                  &ldquo;{brand.marketingLine}&rdquo;
                </p>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{p.blurb}</p>

                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 group-hover:gap-2 transition-all">
                  See what&apos;s tested <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

export default TestPackagesStrip;
